import type { CompositionUnit, UnitRole } from "../../api/types";
import { Select } from "../../components/Select";
import { inputSmClass } from "../../components/ui";
import { isCompany } from "../../company";
import { useGenericEnemy } from "../../enemyDisplay";
import { t } from "../../i18n/i18n";
import { useDeployment } from "../../map/units/useDeployment";
import { defensiveRoles } from "../../map/units/propagation";
import {
  amiRoles,
  autoOption,
  companyRoles,
  defaultMark,
  opoMention,
  roleOption,
  roleShort,
  roleToSend,
  shownRole,
  UNIT_ROLES,
} from "./roles";

/** Sélecteur du rôle d'une unité dans ce dossier (fiche d'unité, docs/18 §6.3) : les options portent le nom court du
 *  rôle (le champ de la fiche est étroit : rien n'y est tronqué) ; la ligne d'aide sous le sélecteur dit la
 *  correspondance avec l'OPO du rôle choisi (« ENI ultérieur dans l'OPO ») et, côté ami, son effet. `onChange` reçoit
 *  le rôle à envoyer (null : rôle par défaut). */
export function RoleSelect({
  unit,
  disabled,
  onChange,
}: {
  unit: CompositionUnit;
  disabled?: boolean;
  onChange: (role: UnitRole | null) => void;
}) {
  const friendly = unit.side === "AMI";
  const neutral = useGenericEnemy();
  const { data: deployment } = useDeployment();
  // Niveau compagnie (docs/21 §4) : rôles des emplacements du S/GTIA, nommés comme dans son ordre.
  const company = isCompany(deployment?.level) && friendly;
  // Rôles d'une défense (registre A126) : ni reconnaissance ; 1er et 2e échelons défensifs, renforts.
  const defensive = friendly && defensiveRoles(deployment);
  const value = shownRole(unit.role, defensive) ?? "";
  const hint = [opoMention(unit, company, defensive), unit.role && friendly ? roleOption("AMI", unit.role, company, defensive).hint : null]
    .filter(Boolean)
    .join(" · ");
  return (
    <span className="block w-full">
      <Select
        value={value}
        disabled={disabled}
        aria-label={t("Rôle de {unit} dans ce dossier", { unit: unit.label })}
        title={t("Rôle dans ce dossier : emplacement de l'unité dans la manœuvre, ses missions et son paragraphe dans l'OPO")}
        className={inputSmClass}
        onChange={(e) => {
          const role = (e.target.value || null) as UnitRole | null;
          const next = roleToSend(role, unit.role_default);
          const current = unit.role_chosen ? unit.role : null;
          if (next !== current) onChange(next);
        }}
      >
        {friendly && unit.role_default === null && <option value="">{autoOption(unit, company, defensive)}</option>}
        {amiRoles(company ? companyRoles(deployment?.company?.roles) : UNIT_ROLES, defensive).map((role) => (
          <option key={role} value={role}>
            {roleShort(role, company, defensive)}
            {role === unit.role_default ? ` (${defaultMark(unit, neutral).toLowerCase()})` : ""}
          </option>
        ))}
      </Select>
      {hint && <span className="mt-1 block text-left text-meta leading-snug text-ink-muted">{hint}</span>}
    </span>
  );
}
